import type {PushNotificationSubscription} from "@inpriton/types";

class PushNotificationService {
  // Long enough that a user reading the browser's permission prompt isn't cut off, short enough
  // that an ignored prompt doesn't strand the join (RISKS.md/P1.15).
  private static readonly REGISTER_TIMEOUT_MS = 15000

  private publicKey: Uint8Array<ArrayBuffer>;

  // RISKS.md/P2.35: handed over by AppContext, which is the only place the worker is registered.
  // Looking it up here by page URL is impossible — see subscribeToPush().
  private registration?: ServiceWorkerRegistration

  constructor(publicKey: string) {
    this.publicKey = this.urlB64ToUint8Array(publicKey)
  }

  public setRegistration(registration: ServiceWorkerRegistration) {
    this.registration = registration
  }

  // RISKS.md/P1.15: this must ALWAYS settle — the join awaits it, so a pending Promise hangs the
  // whole flow. Every branch below resolves or throws, and the time-box is not redundant with that:
  // an IGNORED permission prompt is not a branch any per-path fix can reach. CheckIn treats a
  // rejection as "no push" and joins anyway.
  public register(): Promise<PushNotificationSubscription> {
    return Promise.race([
      this.subscribeToPush(),
      new Promise<never>((_, reject) => setTimeout(
        () => reject(new Error('push registration timed out')),
        PushNotificationService.REGISTER_TIMEOUT_MS,
      )),
    ])
  }

  private async subscribeToPush(): Promise<PushNotificationSubscription> {
    if (!("Notification" in window)) {
      throw new Error('This browser does not support notifications.')
    }

    const permission = await Notification.requestPermission()
    console.log('NOTIFICATIONS', permission)
    if (permission !== "granted") {
      // subscribe() would reject here anyway — but unhandled, which is what hung the join.
      throw new Error(`notification permission ${permission}`)
    }

    // RISKS.md/P2.35: the scope argument is REQUIRED. This worker's scope is '/sw/' (its script
    // directory) and never matches the page ('/join/<token>'), so a bare `getRegistration()`
    // resolves undefined every time. The scope is fine as it is — push needs an ACTIVE worker, not
    // one that CONTROLS the page. Do NOT reach for `navigator.serviceWorker.ready` instead: it also
    // matches the PAGE url and per spec NEVER rejects, so here it hangs forever — the exact join
    // hang P1.15 exists to prevent.
    const registration = this.registration ?? await navigator.serviceWorker.getRegistration('/sw/')
    if (!registration) {
      throw new Error('not registered to service worker')
    }

    // subscribe() rejects with "no active Service Worker" while it is still installing (a
    // first-visit race). Bounded by register()'s timeout, so it cannot stall a join.
    await PushNotificationService.waitForActive(registration)

    const currentSubscription = await registration.pushManager.getSubscription()
    if (currentSubscription) {
      return this.toSubscription(currentSubscription)
    }

    return this.toSubscription(await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: this.publicKey,
    }))
  }

  // Resolves once the registration has an ACTIVE worker. `register()` resolves as soon as the
  // registration exists, so on a first-ever visit it may only be `installing` (RISKS.md/P2.35).
  private static waitForActive(registration: ServiceWorkerRegistration): Promise<void> {
    if (registration.active) {
      return Promise.resolve()
    }

    const worker = registration.installing ?? registration.waiting
    if (!worker) {
      return Promise.reject(new Error('service worker registration has no worker'))
    }

    return new Promise<void>((resolve, reject) => {
      const onStateChange = () => {
        if (worker.state === 'activated') {
          worker.removeEventListener('statechange', onStateChange)
          resolve()
        } else if (worker.state === 'redundant') {
          worker.removeEventListener('statechange', onStateChange)
          reject(new Error('service worker became redundant'))
        }
      }
      worker.addEventListener('statechange', onStateChange)
    })
  }

  private toSubscription(subscription: PushSubscription): PushNotificationSubscription {
    const key = subscription.getKey('p256dh');
    const auth = subscription.getKey('auth');
    return {
      endpoint: subscription.endpoint,
      // @ts-ignore
      key: btoa(String.fromCharCode.apply(null, new Uint8Array(key))),
      // @ts-ignore
      auth: btoa(String.fromCharCode.apply(null, new Uint8Array(auth))),
    }
  }

  private urlB64ToUint8Array(base64String: string) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

let instance: PushNotificationService

export const initPushNotificationService = (publicKey: string) => {
  instance = new PushNotificationService(publicKey)
}

// Deliberately NOT named use* — AppContext calls this from inside initialize(), where a
// use*-prefixed call would trip react-hooks/rules-of-hooks, which fails `npm run lint`.
export const setPushServiceWorkerRegistration = (registration: ServiceWorkerRegistration) => {
  instance?.setRegistration(registration)
}

export const usePushNotificationService = () => {
  if (!instance) {
    throw new Error('push notification service must be initialized by calling initPushNotificationService()')
  }
  return instance
}